import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Clock, MapPin } from 'lucide-react';
import { thisWeekend } from '../data/popups';
import { SkewTag } from './SkewTag';
const ease = [0.23, 1, 0.32, 1] as const;
export function Hero() {
  const reduceMotion = useReducedMotion();
  const w = thisWeekend;
  const badgeDates = w.days.map(d => d.date.replace('Oct ', '')).join(' & ');
  return <section aria-labelledby="hero-heading" className="relative bg-foodCream border-b-4 border-foodMaroon overflow-hidden" data-id="element-96" data-mp="1285ztvqq06q0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-12 gap-12 lg:gap-14 items-center" data-id="element-97" data-mp="x8jmznet0shq">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease
      }} className="lg:col-span-7 order-2 lg:order-1" data-id="element-98" data-mp="ncq2nhuufupe">
          <SkewTag tone="teal" shadow="gold" data-id="element-99" data-mp="381e3bhj8idw5">
            ✦ This Weekend · {w.label}
          </SkewTag>

          <h1 id="hero-heading" className="font-wide text-5xl sm:text-7xl xl:text-8xl uppercase leading-[0.9] mt-7" data-id="element-100" data-mp="3j9k730lk2xau">
            The Pavilion
            <br data-id="element-101" data-mp="39aurjeh4toxr" />
            Turns <span className="text-foodOrange" data-id="element-102" data-mp="399jm7a81g94j">Two.</span>
          </h1>

          <p className="font-display font-wide text-xl sm:text-2xl font-extrabold uppercase mt-5" data-id="element-103" data-mp="3e8wj6nsmiwku">
            We're open more. We're cooking more.
          </p>

          <p className="text-lg text-foodMaroon/85 mt-5 max-w-xl" data-id="element-104" data-mp="30fybq32wpe0x">{w.intro}</p>

          <ul className="flex flex-wrap gap-3 mt-8" aria-label="Celebration dates" data-id="element-105" data-mp="35a2gcfop99yu">
            {w.days.map(d => <li key={d.date} className="bg-foodCard border-2 border-foodMaroon px-4 py-2 text-center min-w-[6rem]" data-id="element-106" data-mp="2qi0kr7tnx34m">
                <span className="block text-xs font-bold uppercase text-foodTaupe" data-id="element-107" data-mp="2hkglg4fd1gwn">
                  {d.weekday}
                </span>
                <span className="block font-display font-wide text-xl font-extrabold uppercase leading-none mt-1" data-id="element-108" data-mp="2lml8ulf7thau">
                  {d.date}
                </span>
              </li>)}
          </ul>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 font-bold" data-id="element-109" data-mp="ic891hh2wayw">
            <span className="inline-flex items-center gap-2" data-id="element-110" data-mp="ia5jsznr2lv8">
              <Clock size={18} aria-hidden="true" className="text-foodTeal" data-id="element-111" data-mp="de6rj0zawjpj" />
              {w.hours}
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-112" data-mp="1b04cr652f356">
              <MapPin size={18} aria-hidden="true" className="text-foodTeal" data-id="element-113" data-mp="161jcx7tq6lh9" />
              India Basin Waterfront Park
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-10" data-id="element-114" data-mp="114kispa2aej2">
            <a href="#menu" className="inline-flex justify-center items-center gap-2 bg-foodOrange text-white px-8 py-4 font-display font-wide font-extrabold uppercase text-lg border-2 border-foodMaroon shadow-[5px_5px_0px_0px_#015C68] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[3px_3px_0px_0px_#015C68] transition-[transform,box-shadow] duration-150 ease-out" data-id="element-115" data-mp="x0cugt7in0ld">
              See the New Menu
              <ArrowRight size={20} aria-hidden="true" data-id="element-116" data-mp="26a9afmitjewq" />
            </a>
            <a href="#hours" className="inline-flex justify-center items-center px-8 py-4 font-display font-wide font-extrabold uppercase text-lg border-2 border-foodMaroon hover:bg-foodMaroon hover:text-foodCream transition-colors duration-150 ease-out" data-id="element-117" data-mp="267wj4ir67s70">
              New Hours
            </a>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease
      }} className="lg:col-span-5 order-1 lg:order-2 relative w-full max-w-lg mx-auto" data-id="element-118" data-mp="2p10f4arv2h4s">
          <div className="bg-foodTeal border-4 border-foodMaroon shadow-[12px_12px_0px_0px_#F6B303] overflow-hidden" data-id="element-119" data-mp="zl0zqbxnfvgp">
            <video src={w.video} autoPlay loop muted playsInline aria-label="The Food Pavilion 2nd anniversary video" className="block w-full h-auto max-h-[80vh] object-contain" data-id="element-120" data-mp="4acm5ch3dp7f" />
          </div>

          <motion.div animate={reduceMotion ? undefined : {
          scale: [1, 1.06, 1]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -top-6 -right-3 sm:-right-8 w-32 h-32 rounded-full bg-foodGold border-4 border-foodMaroon flex flex-col items-center justify-center text-center shadow-[4px_4px_0px_0px_#2A1B14]" data-id="element-121" data-mp="9bihli24zqkn">
            <span className="font-display text-xl font-extrabold uppercase leading-[0.95]" data-id="element-122" data-mp="3moc1g01xnqp4">
              This
              <br data-id="element-123" data-mp="2yyhpdkpfu65x" />
              Weekend
            </span>
            <span className="text-xs font-bold uppercase tracking-wide mt-1.5" data-id="element-124" data-mp="2u17u3ofdb0a6">
              Oct {badgeDates}
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}